import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { HeadlineSmall } from '@shared/ui/atoms/fonts/Headline';
import { BodyLarge, BodyRegular, BodySmall } from '@shared/ui/atoms/fonts/Body';
import { SimpleSpinner } from '@shared/ui/atoms/SimpleSpinner';
import { VisitorBookingSettings } from '@shared/ui/organisms/settings/VisitorBookingSettings';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import Colours from '@shared/ui/atoms/Colours';
import { selectActiveOffices } from '@features/desks/store/advanceHotDeskingSetup.duck';
import { selectConfig } from '@shared/store/config.duck';
import { selectCurrentUser } from '@shared/store/auth.duck';
import { AccountType } from '@shared/models/company.models';
import {
  approveVisitorCheckinPendingRequest,
  generateVisitorCheckinCode,
  listVisitorCheckinCodes,
  listVisitorCheckinPendingRequests,
  rejectVisitorCheckinPendingRequest,
  revokeVisitorCheckinCode,
  updateVisitorCheckinTtl,
  VisitorCheckinCodeListResponse,
  VisitorCheckinCodeView,
  VisitorCheckinPendingRequestView,
} from '../../../services/VisitorCheckinAdminService';
import { HttpError } from '@shared/models/error.models';
import { IsSuperAdmin } from '@shared/hooks/IsSuperAdmin';
import { ConfigurationSection } from '../../StyleComponents';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

const PENDING_POLL_INTERVAL_MS = 5000;

// strt
function formatTtlMinutes(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  if (minutes < 60 * 24) {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    const hoursPart = `${hours} hour${hours === 1 ? '' : 's'}`;
    return remainingMinutes === 0 ? hoursPart : `${hoursPart} ${remainingMinutes} minute${remainingMinutes === 1 ? '' : 's'}`;
  }
  const days = Math.floor(minutes / (60 * 24));
  const remainingHours = Math.floor((minutes % (60 * 24)) / 60);
  const daysPart = `${days} day${days === 1 ? '' : 's'}`;
  return remainingHours === 0 ? daysPart : `${daysPart} ${remainingHours} hour${remainingHours === 1 ? '' : 's'}`;
}

export function VisitorCheckinKioskPanel() {
  const config = useSelector(selectConfig);
  const user = useSelector(selectCurrentUser);
  const isSuperAdmin = IsSuperAdmin();
  const { t } = useTranslation();

  const accountType = user?.companyEntity.accountType;
  const isTierRestricted = accountType === AccountType.STANDARD || accountType === AccountType.PROFESSIONAL;

  return (
    <>
      <HeadlineSmall style={{ marginBottom: 16 }}>{t('settings.visitor-booking.heading')}</HeadlineSmall>

      <VisitorBookingSettings />

      {isSuperAdmin && !isTierRestricted && !!config?.visitorBookingEnabled && <KioskSettings />}
    </>
  );
}

// Kiosk pairing administration — only relevant (and only fetched/polled)
// while visitor booking is enabled.
function KioskSettings() {
  const offices = useSelector(selectActiveOffices);
  const [data, setData] = useState<VisitorCheckinCodeListResponse | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedOfficeId, setSelectedOfficeId] = useState<number | ''>('');
  const [label, setLabel] = useState('');
  const [ttlInput, setTtlInput] = useState('');
  const [urlCopied, setUrlCopied] = useState(false);

  const kioskUrl = `${window.location.origin}/kiosk`;

  const copyKioskUrl = async () => {
    try {
      await navigator.clipboard.writeText(kioskUrl);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    } catch {
      /* clipboard not available */
    }
  };

  useEffect(() => {
    // The code list is shared with Waypoint screens; this panel only manages kiosks.
    listVisitorCheckinCodes().then(d => {
      setData({ ...d, codes: d.codes.filter(c => c.deviceType !== 'SIGNAGE') });
      setTtlInput(d.ttlMinutes ? String(d.ttlMinutes) : '');
    });
  }, []);

  useEffect(() => {
    if (selectedOfficeId === '' && offices.length > 0) {
      setSelectedOfficeId(offices[0].id);
    }
  }, [offices, selectedOfficeId]);

  const handleGenerate = async () => {
    if (selectedOfficeId === '') { setError('Pick an office'); return; }
    setError(null);
    setBusy(true);
    try {
      const created = await generateVisitorCheckinCode(Number(selectedOfficeId), label.trim() || null);
      setData(prev => prev ? { ...prev, codes: [created, ...prev.codes] } : prev);
      setLabel('');
    } catch (err: any) {
      setError(err?.message || 'Could not generate code');
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (id: number) => {
    if (!window.confirm('Revoke this code? Any tablets paired with it will be unpaired.')) return;
    setBusy(true);
    try {
      await revokeVisitorCheckinCode(id);
      setData(prev => prev ? {
        ...prev,
        codes: prev.codes.map(c => c.id === id ? { ...c, revoked: true, activeDeviceCount: 0 } : c),
      } : prev);
    } finally { setBusy(false); }
  };

  const handleSaveTtl = async () => {
    const trimmed = ttlInput.trim();
    const parsed = trimmed === '' ? null : Number(trimmed);
    if (parsed !== null && (Number.isNaN(parsed) || parsed < 1)) {
      setError('TTL must be a positive whole number, or blank to use the default');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const updated = await updateVisitorCheckinTtl(parsed);
      setData(updated);
      setTtlInput(updated.ttlMinutes ? String(updated.ttlMinutes) : '');
    } catch (err: any) {
      setError(err?.message || 'Could not save TTL');
    } finally { setBusy(false); }
  };

  if (!data) return <SimpleSpinner size={32} />;

  const ttlMinutes = ttlInput.trim() === '' ? data.ttlMinutes : Number(ttlInput.trim());
  const ttlDescription = ttlMinutes && !Number.isNaN(ttlMinutes) && ttlMinutes >= 1
    ? `Codes will be valid for ${formatTtlMinutes(Math.floor(ttlMinutes))}.`
    : null;

  return (
    <>
      <PendingRequestsSection />

      <BodyRegular style={{ marginBottom: 24, color: Colours.darkGrey }}>
        Generate an 8-digit pairing code to set up a tablet at a reception desk. Codes are
        single-purpose, multi-use within their lifespan, and bound to a specific office.
      </BodyRegular>

      <InfoBox>
        <BodyLarge weight={600} style={{ marginBottom: 8 }}>How to set up a tablet</BodyLarge>
        <InstructionList>
          <li>
            On the tablet, open this URL in a fullscreen browser:
            <UrlRow>
              <UrlValue>{kioskUrl}</UrlValue>
              <OutlineButton
                text={urlCopied ? 'Copied' : 'Copy link'}
                size={ButtonSize.Small}
                click={copyKioskUrl}
              />
            </UrlRow>
          </li>
          <li>Generate a code below for the office where the tablet will live.</li>
          <li>Enter the 8-digit code on the tablet to pair it.</li>
          <li>The tablet stays paired across reboots until you revoke its code or it&apos;s idle for 5 days.</li>
        </InstructionList>
      </InfoBox>

      <ConfigurationSection>
        <BodyLarge>Pairing code lifespan</BodyLarge>
        <BodyRegular>How long generated codes remain valid (in minutes).</BodyRegular>
        <InlineRow>
          <Input
            type="number"
            min={1}
            value={ttlInput}
            onChange={e => setTtlInput(e.target.value)}
            placeholder={`Default: ${data.ttlMinutes}`}
            disabled={busy}
          />
          <PrimaryButton text={'Save'} size={ButtonSize.Small} click={handleSaveTtl} disabled={busy} />
        </InlineRow>
        {ttlDescription && (
          <BodySmall style={{ marginTop: 8, color: Colours.darkGrey }}>{ttlDescription}</BodySmall>
        )}
      </ConfigurationSection>

      <ConfigurationSection>
        <BodyLarge>Generate a new code</BodyLarge>
        <BodyRegular>The tablet will be permanently bound to the chosen office.</BodyRegular>
        <InlineRow>
          <Select
            value={selectedOfficeId}
            onChange={e => setSelectedOfficeId(e.target.value === '' ? '' : Number(e.target.value))}
            disabled={busy}
          >
            {offices.length === 0 && <option value="">No offices available</option>}
            {offices.map((o: any) => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </Select>
          <Input
            type="text"
            maxLength={80}
            placeholder="Label (optional)"
            value={label}
            onChange={e => setLabel(e.target.value)}
            disabled={busy}
          />
          <PrimaryButton text={'Generate code'} size={ButtonSize.Small} click={handleGenerate} disabled={busy} />
        </InlineRow>
        {error && <ErrorBox>{error}</ErrorBox>}
      </ConfigurationSection>

      <ConfigurationSection>
        <BodyLarge>Codes</BodyLarge>
        {data.codes.length === 0 ? (
          <BodyRegular style={{ marginTop: 12, color: Colours.darkGrey }}>No codes generated yet.</BodyRegular>
        ) : (
          data.codes.map(code => (
            <CodeRow key={code.id} code={code} onRevoke={() => handleRevoke(code.id)} disabled={busy} />
          ))
        )}
      </ConfigurationSection>
    </>
  );
}

function PendingRequestsSection() {
  const [pending, setPending] = useState<VisitorCheckinPendingRequestView[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [conflictMessage, setConflictMessage] = useState<string | null>(null);

  const refresh = async () => {
    try {
      const all = await listVisitorCheckinPendingRequests();
      setPending(all.filter(p => p.deviceType !== 'SIGNAGE'));
    } catch {
      /* swallow polling errors */
    }
  };

  useEffect(() => {
    void refresh();
    const interval = window.setInterval(() => { void refresh(); }, PENDING_POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, []);

  const handleApprove = async (requestId: string) => {
    setBusy(requestId);
    setConflictMessage(null);
    try {
      await approveVisitorCheckinPendingRequest(requestId);
      setPending(prev => prev.filter(p => p.requestId !== requestId));
    } catch (err: any) {
      if (err instanceof HttpError && err.statusCode === 409) {
        setConflictMessage('Already handled — refreshing list');
        await refresh();
      } else {
        setConflictMessage(err?.message || 'Could not approve');
      }
    } finally {
      setBusy(null);
    }
  };

  const handleReject = async (requestId: string) => {
    setBusy(requestId);
    setConflictMessage(null);
    try {
      await rejectVisitorCheckinPendingRequest(requestId);
      setPending(prev => prev.filter(p => p.requestId !== requestId));
    } catch (err: any) {
      if (err instanceof HttpError && err.statusCode === 409) {
        setConflictMessage('Already handled — refreshing list');
        await refresh();
      } else {
        setConflictMessage(err?.message || 'Could not reject');
      }
    } finally {
      setBusy(null);
    }
  };

  if (pending.length === 0) return null;

  return (
    <PendingPanel>
      <BodyLarge weight={600} style={{ marginBottom: 8 }}>
        Pending pairing requests
      </BodyLarge>
      <BodyRegular style={{ marginBottom: 12, color: Colours.darkGrey }}>
        A tablet has entered a code and is waiting for your approval. Requests expire after 5 minutes.
      </BodyRegular>
      {conflictMessage && <ErrorBox>{conflictMessage}</ErrorBox>}
      {pending.map(p => (
        <PendingRow
          key={p.requestId}
          request={p}
          disabled={busy !== null}
          onApprove={() => handleApprove(p.requestId)}
          onReject={() => handleReject(p.requestId)}
        />
      ))}
    </PendingPanel>
  );
}

function PendingRow({ request, disabled, onApprove, onReject }: { request: VisitorCheckinPendingRequestView; disabled: boolean; onApprove: () => void; onReject: () => void }) {
  const remaining = usePendingCountdown(request.expiresAt);
  return (
    <PendingRowWrap>
      <CodeRowHeader>
        <CodeValue>{request.code}</CodeValue>
        {request.label && <CodeLabel>{request.label}</CodeLabel>}
        <Pill $variant="ok">Waiting</Pill>
      </CodeRowHeader>
      <CodeRowMeta>
        <BodySmall style={{ color: Colours.darkGrey }}>
          Office: {request.officeLabel || `#${request.officeId}`}
          {request.clientIp ? ` · From ${request.clientIp}` : ''}
          {remaining !== null ? ` · Expires in ${formatRemaining(remaining)}` : ''}
        </BodySmall>
      </CodeRowMeta>
      <CodeRowActions>
        <PrimaryButton text={'Approve'} size={ButtonSize.Small} click={onApprove} disabled={disabled} />
        <OutlineButton text={'Reject'} size={ButtonSize.Small} click={onReject} disabled={disabled} />
      </CodeRowActions>
    </PendingRowWrap>
  );
}

function usePendingCountdown(expiresAt: string): number | null {
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = window.setInterval(() => setTick(t => t + 1), 1000);
    return () => window.clearInterval(interval);
  }, []);
  const ms = new Date(expiresAt).getTime() - Date.now();
  return ms > 0 ? ms : 0;
}

function formatRemaining(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

interface CodeRowProps {
  code: VisitorCheckinCodeView;
  onRevoke: () => void;
  disabled: boolean;
}

function CodeRow({ code, onRevoke, disabled }: CodeRowProps) {
  const expiry = new Date(code.expiresAt);
  const expired = expiry.getTime() < Date.now();

  return (
    <CodeRowWrap>
      <CodeRowHeader>
        <CodeValue>{code.code}</CodeValue>
        {code.label && <CodeLabel>{code.label}</CodeLabel>}
        {code.revoked && <Pill $variant="danger">Revoked</Pill>}
        {!code.revoked && expired && <Pill $variant="muted">Expired</Pill>}
        {!code.revoked && !expired && <Pill $variant="ok">Active</Pill>}
      </CodeRowHeader>
      <CodeRowMeta>
        <BodySmall style={{ color: Colours.darkGrey }}>
          Office: {code.officeLabel || `#${code.officeId}`} · Expires {expiry.toLocaleString()} · {code.activeDeviceCount} active tablet{code.activeDeviceCount === 1 ? '' : 's'}
        </BodySmall>
      </CodeRowMeta>
      {!code.revoked && (
        <CodeRowActions>
          <OutlineButton text={'Revoke'} size={ButtonSize.Small} click={onRevoke} disabled={disabled} />
        </CodeRowActions>
      )}
    </CodeRowWrap>
  );
}

const PendingPanel = styled.div`
  margin-bottom: 24px;
  padding: 16px 20px;
  background: ${Colours.orange10};
  border: 1px solid ${Colours.orange20};
  border-radius: 8px;
`;

const PendingRowWrap = styled.div`
  margin-top: 12px;
  padding: 12px;
  background: white;
  border: 1px solid ${Colours.lightGrey};
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const InfoBox = styled.div`
  margin-bottom: 24px;
  padding: 16px 20px;
  background: ${Colours.blue05};
  border: 1px solid ${Colours.blue20};
  border-radius: 8px;
`;

const InstructionList = styled.ol`
  margin: 8px 0;
  padding-left: 20px;
  color: ${Colours.black};
  font-size: 14px;
  line-height: 1.6;
  li {
    margin-bottom: 4px;
  }
`;

const UrlRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin: 8px 0 4px 0;
  flex-wrap: wrap;
`;

const UrlValue = styled.div`
  padding: 8px 12px;
  background: white;
  border: 1px solid ${Colours.mildGrey};
  border-radius: 4px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 14px;
  word-break: break-all;
  flex: 1;
  min-width: 240px;
`;

const InlineRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const Input = styled.input`
  height: 32px;
  padding: 0 10px;
  border: 1px solid ${Colours.mildGrey};
  border-radius: 4px;
  font-size: 14px;
  min-width: 200px;
`;

const Select = styled.select`
  height: 32px;
  padding: 0 10px;
  border: 1px solid ${Colours.mildGrey};
  border-radius: 4px;
  font-size: 14px;
  min-width: 220px;
  background: white;
`;

const ErrorBox = styled.div`
  margin-top: 12px;
  padding: 8px 12px;
  border: 1px solid ${Colours.red};
  color: ${Colours.red};
  border-radius: 4px;
  font-size: 13px;
`;

const CodeRowWrap = styled.div`
  margin-top: 12px;
  padding: 12px;
  border: 1px solid ${Colours.lightGrey};
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const CodeRowHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const CodeRowMeta = styled.div``;

const CodeRowActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;
`;

const CodeValue = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 2px;
`;

const CodeLabel = styled.span`
  font-family: proxima-nova;
  font-size: 14px;
  color: ${Colours.darkGrey};
`;

const Pill = styled.span<{ $variant: 'ok' | 'muted' | 'danger' }>`
  font-family: proxima-nova;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${p => p.$variant === 'ok' ? Colours.green : (p.$variant === 'danger' ? Colours.red : Colours.iconGrey)};
  color: ${p => p.$variant === 'muted' ? Colours.darkGrey : 'white'};
`;
